import { useMemo } from "react";
import { useSelector } from "react-redux";
import { BaseAxisProps } from "recharts/types/util/types";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  ComponentRenderLocation,
  GraphConfig,
  GraphGlobalMetric,
  LineData,
} from "@intelligems/ig-types";
import { Stack } from "@mui/system";
import { GraphHeader } from "@src/features/analytics/baseDashboard/chartComponents/GraphHeader";
import { ChartWrapper } from "@src/features/analytics/baseDashboard/charts/ChartWrapper";
import {
  SitewideAnalyticsAudienceQueryResult,
  SitewideAnalyticsHolidayBenchmarkingQueryResult,
  SitewideAnalyticsPerformanceQueryResult,
} from "@src/features/analytics/types";
import { selectedVariations } from "@src/store/slices/variations";
import {
  BarMetricData,
  GraphMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";
import { UseQueryResult } from "@tanstack/react-query";

import { METRIC_TO_LEGEND_LABEL } from "../../utils";

interface GraphsWrapperProps {
  renderLocation: ComponentRenderLocation;
  data?: GraphMetricData;
  config: GraphConfig;
  totalTestVisitors?: number;
  metricFilterIndex?: number;
  isSharing?: boolean;
  focusedVariationId?: string;
  hideHeader?: boolean;
  syncId?: string;
  filterMethod?: string;
  focusGroups?: string[];
  margin?: { top?: number; bottom?: number; right?: number; left?: number };
  testDuration: number;
  onMetricDetailsClick?: () => void;
  onAddKeyMetricClick?: () => void;
  isDataLoading: boolean;
  globalMetrics?: GraphGlobalMetric[];
  compareVariationId?: string;
  setBarCellColor?: (params: {
    compareVariationId?: string;
    data: BarMetricData;
    index: number;
  }) => string;
  setStackedBarCellColor?: (params: {
    compareVariationId?: string;
    data: StackedBarMetricData;
    index: number;
  }) => string;
  rawData?:
    | UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[]
    | SitewideAnalyticsAudienceQueryResult
    | SitewideAnalyticsPerformanceQueryResult
    | SitewideAnalyticsHolidayBenchmarkingQueryResult;
  lines?: LineData[];
  showWatermark?: boolean;
  watermarkPosition?: "left" | "right";
  xAxisProps?: BaseAxisProps;
  yAxisProps?: BaseAxisProps;
  dateFormat?: string;
  intervalOfTheData?: number;
  graphHeight?: number;
}
export const GraphsWrapper = ({
  data,
  config,
  totalTestVisitors,
  metricFilterIndex,
  isSharing,
  renderLocation,
  focusedVariationId,
  hideHeader,
  syncId,
  filterMethod,
  focusGroups,
  margin,
  onAddKeyMetricClick,
  onMetricDetailsClick,
  testDuration,
  isDataLoading,
  globalMetrics,
  compareVariationId,
  setBarCellColor,
  setStackedBarCellColor,
  rawData,
  lines,
  showWatermark = true,
  watermarkPosition,
  xAxisProps,
  yAxisProps,
  dateFormat,
  graphHeight,
}: GraphsWrapperProps) => {
  const variations = useSelector(selectedVariations);

  const variationsLines = useMemo(() => {
    if (lines || !variations || renderLocation === "sitewide") return;
    return variations.map((variation) => {
      // For personalizations the variation name is usually "control group"
      // therefore the metric offers a better label to show as legend
      if (renderLocation === "personalization") {
        const metric = config.metrics[0];
        return { id: variation.id, name: METRIC_TO_LEGEND_LABEL[metric] };
      }
      return { id: variation.id, name: variation.name };
    });
  }, [lines, renderLocation, variations]);

  return (
    <Stack
      spacing={6}
      sx={{ justifyContent: "space-between", height: "100%", width: "100%" }}
    >
      {!hideHeader && (
        <GraphHeader
          groupMetricData={data}
          config={config}
          metricFilterIndex={metricFilterIndex}
          isDataLoading={isDataLoading}
          globalMetrics={globalMetrics}
          testDuration={testDuration}
        />
      )}

      <ChartWrapper
        id={isSharing ? "ig-sharing-graph" : undefined}
        config={config}
        renderLocation={renderLocation}
        data={data}
        isSharing={isSharing}
        focusedVariationId={focusedVariationId || lines?.[0]?.id}
        syncId={syncId}
        filterMethod={filterMethod}
        focusGroups={focusGroups}
        margin={margin}
        totalTestVisitors={totalTestVisitors || 0}
        onAddKeyMetricClick={onAddKeyMetricClick}
        onMetricDetailsClick={onMetricDetailsClick}
        testDuration={testDuration}
        isDataLoading={isDataLoading}
        compareVariationId={compareVariationId}
        setBarCellColor={setBarCellColor}
        setStackedBarCellColor={setStackedBarCellColor}
        rawData={rawData}
        lines={lines || variationsLines}
        showWatermark={showWatermark}
        watermarkPosition={watermarkPosition}
        xAxisProps={xAxisProps}
        yAxisProps={yAxisProps}
        intervalOfTheData={config.intervalOfTheData}
        dateFormat={dateFormat}
        graphHeight={graphHeight}
      />
    </Stack>
  );
};
